import { useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Loader2, Ruler, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import ConfidenceChip from "./ConfidenceChip";

/**
 * Tiny admin-only chip + dialog to verify (or override) a booking's square
 * footage and recompute price in one click.
 *
 * Renders inline as a chip showing:
 *    Est <est> · Verified <ver> · Δ <diff>
 * and opens a dialog where the admin can set the verified sqft + recompute.
 */
export default function BookingSqftVerifyButton({ booking, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [verifiedSqft, setVerifiedSqft] = useState(booking?.verified_sqft || booking?.square_feet || 0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const estimated = Number(booking?.estimated_sqft ?? booking?.square_feet ?? 0);
  const verified = booking?.sqft_verified ? Number(booking?.verified_sqft || 0) : null;
  const diff = verified != null ? verified - estimated : null;

  const submit = async () => {
    const n = Number(verifiedSqft);
    if (!n || n < 1) return toast.error("Enter a valid square footage");
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/bookings/${booking.booking_id}/verify-sqft`, {
        verified_sqft: n,
        note: note || null,
      });
      const dp = (data.price_diff || 0).toFixed(2);
      toast.success(`Verified ${data.verified_sqft.toLocaleString()} sqft · price ${dp >= 0 ? "+" : ""}$${dp}`);
      setOpen(false);
      onUpdated?.(data.booking);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusy(false); }
  };

  const diffColor = diff == null
    ? "text-gray-500 bg-gray-100 border-gray-200"
    : diff > 0
      ? "text-amber-800 bg-amber-100 border-amber-200"
      : diff < 0
        ? "text-rose-800 bg-rose-100 border-rose-200"
        : "text-emerald-800 bg-emerald-100 border-emerald-200";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`verify-sqft-btn-${booking?.booking_id}`}
        className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.12em] px-2 py-1 rounded-full border bg-white text-gray-700 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
        title="Verify / override square footage"
      >
        <Ruler className="w-3 h-3" />
        <span data-testid={`verify-sqft-est-${booking?.booking_id}`}>Est {estimated.toLocaleString()}</span>
        {verified != null && (
          <>
            <ArrowRight className="w-3 h-3 opacity-60" />
            <span data-testid={`verify-sqft-ver-${booking?.booking_id}`}>{verified.toLocaleString()}</span>
            <span className={`ml-1 px-1.5 py-0.5 rounded-full border ${diffColor}`} data-testid={`verify-sqft-diff-${booking?.booking_id}`}>
              {diff > 0 ? "+" : ""}{diff?.toLocaleString()}
            </span>
          </>
        )}
        {verified == null && (
          <span className="ml-1 px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200">Unverified</span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="verify-sqft-dialog">
          <DialogHeader>
            <DialogTitle>Verify property square footage</DialogTitle>
            <DialogDescription>
              Set the verified yard size for this booking. Price will be recomputed automatically using the canonical JDSN pricing engine.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Estimated" value={`${estimated.toLocaleString()} sqft`} />
              <Stat label="Current price" value={`$${(booking?.price || 0).toFixed(2)}`} />
            </div>
            {(booking?.total_lot_sqft || booking?.house_sqft || booking?.mowable_sqft) && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 px-3 py-3 space-y-2" data-testid="mowable-breakdown">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-extrabold text-emerald-800">Mowable area breakdown</div>
                  {booking?.mowable_confidence && (
                    <ConfidenceChip confidence={booking.mowable_confidence} pct={booking.mowable_confidence_pct} />
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <BreakdownRow label="Total lot"    value={booking?.total_lot_sqft} />
                  <BreakdownRow label="House"        value={booking?.house_sqft} />
                  <BreakdownRow label="Mowable lawn" value={booking?.mowable_sqft} accent />
                </div>
              </div>
            )}
            <div>
              <Label>Verified sqft</Label>
              <Input
                type="number"
                min={1}
                max={200000}
                value={verifiedSqft}
                onChange={(e) => setVerifiedSqft(e.target.value)}
                className="mt-1.5 text-lg font-extrabold tabular-nums"
                data-testid="verify-sqft-input"
              />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="On-site measurement, plat map, etc."
                className="mt-1.5"
                data-testid="verify-sqft-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="rounded-full bg-[#047857] hover:bg-[#065F46]" data-testid="verify-sqft-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update price"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-gray-50 border border-gray-200 px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">{label}</div>
      <div className="mt-0.5 text-base font-extrabold tabular-nums text-gray-900">{value}</div>
    </div>
  );
}

function BreakdownRow({ label, value, accent = false }) {
  return (
    <div className="rounded-xl bg-white border border-emerald-100 px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-[0.12em] font-extrabold text-gray-500">{label}</div>
      <div className={`mt-0.5 text-[13px] font-extrabold tabular-nums ${accent ? "text-[#047857]" : "text-gray-900"}`}>
        {value ? `${Number(value).toLocaleString()} sqft` : "—"}
      </div>
    </div>
  );
}
